import { useSuspenseQuery } from "@tanstack/react-query";
import { differenceInMonths, endOfMonth, startOfMonth } from "date-fns";
import { groupBy, orderBy, round, sum, uniqBy } from "lodash-es";
import { useState } from "react";
import { useNavigate } from "react-router";

import type { MonthlyReportDateRangeDto, TicketsBenchmarkItemDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { LegendDataIcon, type LegendDataIconProps } from "@/components/Charts/LegendDataIcon";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { NoData } from "@/components/NoData/NoData";
import { avgBy } from "@/helpers/math";
import { isDefined } from "@/helpers/util";
import { useProject } from "@/hooks/Network/useProject";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSlug } from "@/hooks/useSlug";
import { BarChart } from "@/modules/analytics/components/BarChart";
import type { AnalyticsRouteParams } from "@/modules/analytics/routes";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { CLOSED_TICKET_TAB } from "@/queries/tickets/constants";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { AnalyticsCard } from "../../../components/AnalyticsCard";
import { type DateRangeOption, filterDataByDateRange } from "../../../util";

interface Props {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}

const DEFAULT_DATE_RANGE: DateRangeOption["value"] = "lastYear";

export function TicketSatisfactionCard({ dateRangeOptions }: Props): React.ReactNode {
  const clockDate = useCurrentDate();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const project = useProject();
  const analyticsQueries = useAnalyticsQueries();
  const [queryParams, setQueryParams] = useQueryParams<AnalyticsRouteParams["overview"]>();

  let defaultDateRange: DateRangeOption | undefined = dateRangeOptions[0];
  if (queryParams["tickets-date-range"]) {
    if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 6) {
      defaultDateRange = dateRangeOptions.find((d) => d.value === "last6Months");
    } else if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 12) {
      defaultDateRange = dateRangeOptions.find((d) => d.value === "lastYear");
    }
  }
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(defaultDateRange || dateRangeOptions[0]);
  const [category, setCategory] = useState<{
    name: string;
    id: string;
  }>();
  const { data } = useSuspenseQuery(analyticsQueries.ticketRatingMonthly(DEFAULT_DATE_RANGE));

  const handleBarClick = (range: MonthlyReportDateRangeDto, categoryId?: string) => {
    const params = new URLSearchParams({
      tab: CLOSED_TICKET_TAB,
      ratedFrom: range.from.slice(0, 10),
      ratedTo: range.to.slice(0, 10),
    });
    if (categoryId) {
      params.set("categoryIds", categoryId);
    }

    void navigate({ pathname: routes.tickets.list({ slug }), search: params.toString() });
  };

  const handleSelectDateRange = (item: DateRangeOption) => {
    setSelectedDateRange(item);
    setQueryParams({
      ...queryParams,
      "tickets-date-range": undefined,
    } satisfies AnalyticsRouteParams["overview"]);
  };

  let selectedDateRangeOverride = selectedDateRange;
  if (queryParams["tickets-date-range"]) {
    if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 6) {
      selectedDateRangeOverride = dateRangeOptions[0];
    } else if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 12) {
      selectedDateRangeOverride = dateRangeOptions[1];
    }
  }
  const categories = uniqBy(
    data.data.map((x) => ({
      name: x.categoryName,
      id: x.categoryId,
    })),
    (x) => x.id,
  );
  const benchmark = filterDataByDateRange(
    [
      ...data.benchmark,
      {
        range: {
          year: startOfMonth(clockDate).getFullYear(),
          month: startOfMonth(clockDate).getMonth() + 1,
          from: startOfMonth(clockDate).toISOString(),
          to: endOfMonth(clockDate).toISOString(),
        },
        averageRating: undefined,
        ticketCount: 0,
      } as TicketsBenchmarkItemDto,
    ].map((d) => ({
      x: parseChartDate(d.range).toISOString(),
      y: d.averageRating,
      range: d.range,
    })),
    selectedDateRangeOverride,
  );

  const primaryData = filterDataByDateRange(
    orderBy(Object.entries(groupBy(data.data, (x) => parseChartDate(x.range).toISOString())), (x) => x[0]).map(
      ([date, values]) => {
        const average = avgBy(
          values,
          (x) => x.averageRating,
          (x) => x.ratedTicketCount,
        );

        return {
          x: date,
          y: isDefined(average) ? round(average, 2) : undefined,
          ticketCount: sum(values.map((x) => x.ratedTicketCount)),
          isPending: values.every((x) => x.isCurrentMonth),
          range: values[0].range,
        };
      },
    ),
    selectedDateRangeOverride,
  );

  const groupedByCategory = Object.fromEntries(
    Object.entries(
      groupBy(
        data.data.map((d) => ({
          x: parseChartDate(d.range).toISOString(),
          y: d.averageRating,
          categoryName: d.categoryName,
          categoryId: d.categoryId,
          ticketCount: d.ratedTicketCount,
          range: d.range,
        })),
        (d) => d.categoryId,
      ),
    ).map((x) => [x[0], filterDataByDateRange(x[1], selectedDateRangeOverride)]),
  );

  const noData = data.data.every((x) => x.averageRating === undefined);

  return (
    <AnalyticsCard.Root variant="graph">
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.avg-ticket-rating.title")}
        tooltip={t("page.analytics-overview.chart.avg-ticket-rating.title-tooltip")}
      >
        <AnalyticsCard.FilterSelect
          placeholder={t("page.analytics-overview.category-placeholder")}
          emptyItem={t("page.analytics-overview.category-deselect")}
          items={categories}
          selected={category}
          onChange={setCategory}
          renderOption={(x) => x.name}
          keySelector={(x) => x.id}
        />
        <AnalyticsCard.FilterSelect
          items={dateRangeOptions}
          selected={selectedDateRangeOverride}
          onChange={handleSelectDateRange}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <>
          <BarChart
            dataPrimary={{
              data: primaryData,
              onClick: (currDataPoint) => {
                handleBarClick(currDataPoint.range);
              },
            }}
            dataBenchmark={benchmark}
            dataSecondary={
              category
                ? {
                    data: groupedByCategory[category.id],
                    onClick: (point) => handleBarClick(point.range, point.categoryId),
                  }
                : undefined
            }
            maxY={data.maxRating}
            formatYTick={(y) => `${y} ★`}
            xAxisType="date"
            heightAuto
            hoverLabel={(dataPoint, isSecondary) => (
              <table>
                <tbody>
                  <TicketCountRow
                    ticketCount={(isSecondary ? dataPoint.secondary?.ticketCount : dataPoint.primary?.ticketCount) || 0}
                  />
                  <StarRow
                    title={project.name}
                    value={dataPoint.primary?.y}
                    icon={{
                      fill: dataColors.primary,
                      type: "square",
                    }}
                  />
                  {category && (
                    <StarRow
                      title={category?.name || "-"}
                      value={dataPoint.secondary?.y}
                      icon={{
                        fill: dataColors.green,
                        type: "square",
                      }}
                    />
                  )}
                  <StarRow
                    title={t("common.label.aop-benchmark")}
                    value={dataPoint.benchmark?.y}
                    icon={{
                      fill: dataColors.benchmark,
                      type: "minus",
                    }}
                  />
                  {dataPoint.primary?.isPending && (
                    <tr>
                      <td colSpan={4} className="text-caption text-text-default-inverse">
                        {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          />
          <ChartLegend
            items={[
              {
                label: project.name,
                color: dataColors.primary,
              },
              ...(category
                ? [
                    {
                      label: category.name,
                      color: dataColors.green,
                    },
                  ]
                : []),
              {
                label: t("common.label.aop-benchmark"),
                color: dataColors.benchmark,
              },
              {
                label: t("page.analytics-details.engagement.chart.legend.current-period"),
                color: dataColors.primary,
                pattern: "striped",
              },
            ]}
          />
        </>
      )}
    </AnalyticsCard.Root>
  );
}
function TicketCountRow({ ticketCount }: { ticketCount: number }) {
  const { t } = useTranslation();

  return (
    <tr>
      <td colSpan={4}>
        {t("page.analytics-overview.chart.avg-ticket-rating.datapoint-label", {
          amount: ticketCount,
        })}
      </td>
    </tr>
  );
}
function StarRow({ title, icon, value }: { title: string; icon: LegendDataIconProps; value?: number }) {
  return (
    <tr>
      <td className="pr-space-sm">
        <LegendDataIcon {...icon} />
      </td>
      <td>{title}</td>
      <td className="pl-space-sm">
        <Star01Icon tone="rating" />
      </td>
      <td>
        <strong>{value ?? "-"}</strong>
      </td>
    </tr>
  );
}
